import type { Course, Fluency, Question, ReadingCheck, Unit } from '@/types';

const allModules = import.meta.glob<{ default: unknown }>('../content/**/*.json', { eager: true });

const rawUnits: Unit[] = [];
const fluencyByCourse = new Map<string, Fluency>();
const readingCheckByUnitId = new Map<string, ReadingCheck>();
const slugByCourse = new Map<string, string>();

for (const [path, mod] of Object.entries(allModules)) {
  const slug = path.split('/').slice(-2, -1)[0];
  const filename = path.split('/').pop()!.replace('.json', '');

  if (filename === 'fluency') {
    const fluency = mod.default as Fluency;
    fluencyByCourse.set(fluency.courseCode, fluency);
    slugByCourse.set(fluency.courseCode, slug);
  } else if (filename.endsWith('-readingcheck')) {
    const rc = mod.default as ReadingCheck;
    readingCheckByUnitId.set(rc.unitId, rc);
  } else {
    const unit = mod.default as Unit;
    rawUnits.push(unit);
    slugByCourse.set(unit.courseCode, slug);
  }
}

export const allUnits: Unit[] = rawUnits
  .map((u) => ({ ...u, readingCheck: readingCheckByUnitId.get(u.id) ?? undefined }))
  .sort((a, b) => a.courseCode.localeCompare(b.courseCode) || a.unitNumber - b.unitNumber);

/**
 * A course's final exam is a synthetic unit whose question pool is every real
 * unit's questions, each tagged with the unit it came from. It is deliberately
 * kept out of `allUnits` and `course.units` so it never renders as a unit.
 */
function buildExam(course: Course): Unit | null {
  const populated = course.units.filter((u) => u.questions.length > 0);
  if (populated.length < 2) return null;

  const questions: Question[] = populated.flatMap((u) =>
    u.questions.map((q) => ({ ...q, srcUnit: `Unit ${u.unitNumber}`, srcUnitId: u.id })),
  );

  return {
    id: `${course.slug}-final`,
    courseCode: course.code,
    courseTitle: course.title,
    unitNumber: 0,
    unitTitle: 'Final Exam',
    version: 1,
    questions,
    isExam: true,
    examUnits: populated.map((u) => ({
      id: u.id,
      unitNumber: u.unitNumber,
      unitTitle: u.unitTitle,
      count: u.questions.length,
    })),
  };
}

export const allCourses: Course[] = (() => {
  const byCode = new Map<string, Course>();
  for (const u of allUnits) {
    let c = byCode.get(u.courseCode);
    if (!c) {
      c = {
        slug: slugByCourse.get(u.courseCode) ?? u.courseCode.toLowerCase().replace(/\s+/g, ''),
        code: u.courseCode,
        title: u.courseTitle,
        units: [],
        fluency: fluencyByCourse.get(u.courseCode) ?? null,
        exam: null,
      };
      byCode.set(u.courseCode, c);
    }
    c.units.push(u);
  }
  const courses = [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code));
  for (const c of courses) c.exam = buildExam(c);
  return courses;
})();

const examsById = new Map<string, Unit>(
  allCourses.filter((c) => c.exam).map((c) => [c.exam!.id, c.exam!]),
);

export function findUnit(unitId: string): Unit | undefined {
  return allUnits.find((u) => u.id === unitId) ?? examsById.get(unitId);
}

export function findCourseBySlug(slug: string): Course | undefined {
  return allCourses.find((c) => c.slug === slug);
}

export function findCourseForUnit(unitId: string): Course | undefined {
  return allCourses.find(
    (c) => c.exam?.id === unitId || c.units.some((u) => u.id === unitId),
  );
}
